// Component tombstones (Michael Feathers' dead-code detection): records which Vue
// components a real user actually mounts and ships the set to the telemetry
// endpoint (POST /api/telemetry/component-access), tagged with browser_id +
// correlation_id, so Overseer can bump each component's last_used_at and surface
// components that ship in the app but are never accessed by a customer (dead UI).
//
// Companion to clientLogger.ts and shares its transport rules:
//  - fetch (NOT the global axios) to avoid the 401-reload interceptor;
//  - only ships once an access token is present (buffers until login otherwise);
//  - batched + throttled, with backoff on failure and a final flush on page hide.
//
// Component identity comes from `__componentId`, stamped at build time by the
// tombstoneComponentId() Vite plugin (vite.config.ts); `__file` is only a dev
// fallback since plugin-vue strips it from production builds.

import type { App } from "vue";
import Cookies from "js-cookie";
import { getBrowserId } from "@/telemetry/browserId";

const ENDPOINT = "/api/telemetry/component-access";
const FLUSH_INTERVAL = 30_000;
const BATCH_SIZE = 200;
const BACKOFF_BASE = 15_000;
const BACKOFF_MAX = 5 * 60_000;

// Every component id seen this session, and the subset not yet acknowledged by
// the server. We only ever report each id once (a tombstone, not a hit counter).
const accessed = new Set<string>();
const pending = new Set<string>();

let flushing = false;
let backoffUntil = 0;
let consecutiveFailures = 0;
let installed = false;

function record(componentId: string): void {
  if (accessed.has(componentId)) return;
  accessed.add(componentId);
  pending.add(componentId);
}

function componentIdOf(options: Record<string, unknown>): string | null {
  const id = options.__componentId;
  if (typeof id === "string" && id) return id;
  const file = options.__file;
  if (typeof file === "string" && file) return file;
  return null;
}

function scheduleBackoff(): void {
  consecutiveFailures += 1;
  const delay = Math.min(BACKOFF_BASE * 2 ** (consecutiveFailures - 1), BACKOFF_MAX);
  backoffUntil = Date.now() + delay;
}

async function flush(useKeepalive = false): Promise<void> {
  if (flushing || pending.size === 0 || Date.now() < backoffUntil) return;

  // Only ship once authenticated; otherwise keep accumulating (bounded by the
  // finite number of components) until login.
  const token = Cookies.get("access_token");
  if (!token) return;

  flushing = true;
  const batch = [...pending].slice(0, BATCH_SIZE);
  try {
    const response = await fetch(ENDPOINT, {
      method: "POST",
      credentials: "same-origin",
      keepalive: useKeepalive,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-Browser-ID": getBrowserId(),
        Authorization: token,
      },
      body: JSON.stringify({ components: batch }),
    });
    if (response.ok) {
      consecutiveFailures = 0;
      batch.forEach((id) => pending.delete(id));
    } else {
      scheduleBackoff();
    }
  } catch {
    // Telemetry must never throw into the app; just back off and retry later.
    scheduleBackoff();
  } finally {
    flushing = false;
  }
}

export default {
  install(app: App): void {
    if (installed || typeof window === "undefined") return;
    installed = true;

    app.mixin({
      mounted() {
        try {
          const id = componentIdOf(this.$options as Record<string, unknown>);
          if (id) record(id);
        } catch {
          // component tracking must never break the app
        }
      },
    });

    window.setInterval(() => void flush(), FLUSH_INTERVAL);
    const onHide = (): void => void flush(true);
    window.addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") onHide();
    });
  },
};
